/**
 * DynamicScreenRenderer — composes an array of { type, props } component specs
 * from the screen_component_registry into a vertical stack of sub-panels.
 *
 * Supported component types (mirrors screen_component_registry):
 *   metric_row   — row of labeled metric cards
 *   data_table   — sortable/filterable HTML table
 *   action_bar   — row of action buttons (with optional fetch + confirm)
 *   text_block   — paragraph or heading
 *   chart        — CSS bar-chart fallback (no external deps)
 */
import React from 'react';
import { ActionBar } from './ArtifactRenderer';
import './DynamicScreenRenderer.css';

// ── Sub-renderers ─────────────────────────────────────────────────────────────

function MetricRow({ props: p = {} }) {
    const metrics = p.metrics || p.items || [];
    return (
        <div className="dsr-metric-row">
            {metrics.map((m, i) => (
                <div key={i} className="metric-card">
                    <span className="metric-card-label">{m.label ?? m.name ?? `Metric ${i + 1}`}</span>
                    <span className="metric-card-value">{m.value ?? m.val ?? '—'}</span>
                    {m.unit && <span className="metric-card-unit">{m.unit}</span>}
                    {m.delta != null && (
                        <span className={`metric-card-delta${Number(m.delta) >= 0 ? '' : ' metric-card-delta--neg'}`}>
                            {Number(m.delta) >= 0 ? '+' : ''}{m.delta}
                        </span>
                    )}
                </div>
            ))}
        </div>
    );
}

function DataTablePanel({ props: p = {} }) {
    const columns = p.columns || [];
    const rows    = p.rows    || [];
    if (!columns.length && !rows.length) {
        return <p className="dsr-empty">No data available.</p>;
    }
    return (
        <div className="dsr-table-wrap">
            <table className="artifact-table">
                <thead>
                    <tr>
                        {columns.map((col, i) => (
                            <th key={i}>{typeof col === 'object' ? (col.header || col.field || col.label || col.key || String(i)) : col}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, ri) => (
                        <tr key={ri}>
                            {columns.map((col, ci) => {
                                const key = typeof col === 'object' ? (col.field || col.key || ci) : col;
                                const val = Array.isArray(row) ? row[ci] : row[key];
                                return <td key={ci}>{val == null ? '' : String(val)}</td>;
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function TextBlock({ props: p = {} }) {
    const content = p.content || p.text || '';
    const variant = p.variant || 'body';
    if (variant === 'heading' || variant === 'h6') {
        return <h6 className="dsr-heading">{content}</h6>;
    }
    return <p className="dsr-paragraph">{content}</p>;
}

function ChartPanel({ props: p = {} }) {
    const label  = p.x_axis   || p.label_key  || 'label';
    const value  = p.y_axis   || p.value_key  || 'value';
    const series = p.series   || p.data       || [];

    if (!series.length) return <p className="dsr-empty">No chart data.</p>;

    const maxVal = Math.max(...series.map(d => Number(d[value] ?? 0) || 0), 1);

    return (
        <div className="dsr-chart">
            {series.map((d, i) => {
                const pct = ((Number(d[value] ?? 0) / maxVal) * 100).toFixed(1);
                return (
                    <div key={i} className="artifact-chart-row">
                        <span className="artifact-chart-label">{d[label] ?? i}</span>
                        <div className="artifact-chart-bar-track">
                            <div className="artifact-chart-bar" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="artifact-chart-value">{d[value] ?? ''}</span>
                    </div>
                );
            })}
        </div>
    );
}

// ── Component map ─────────────────────────────────────────────────────────────

const PANEL_MAP = {
    metric_row:  (spec) => <MetricRow    props={spec.props} />,
    data_table:  (spec) => <DataTablePanel props={spec.props} />,
    action_bar:  (spec) => <ActionBar    payload={spec.props} />,
    text_block:  (spec) => <TextBlock    props={spec.props} />,
    chart:       (spec) => <ChartPanel   props={spec.props} />,
};

// ── DynamicScreenRenderer ─────────────────────────────────────────────────────

export default function DynamicScreenRenderer({ components = [] }) {
    if (!components.length) {
        return <p className="dsr-empty">Empty screen — no components defined.</p>;
    }

    return (
        <div className="dynamic-screen">
            {components.map((spec, idx) => {
                const render = PANEL_MAP[spec.type];
                return (
                    <div key={idx} className={`dsr-panel dsr-panel--${spec.type || 'unknown'}`}>
                        {spec.title && <div className="dsr-panel-title">{spec.title}</div>}
                        {render
                            ? render(spec)
                            : <div className="artifact-unknown">Unknown screen component: {spec.type}</div>
                        }
                    </div>
                );
            })}
        </div>
    );
}
